import { apiService } from '@/services';
import { useMutation, useQuery } from '@tanstack/react-query';
import queryClient from '@/query-client';
import { markPullInitiated } from './listing.initiator';
import type {
  ListingResponse,
  ListingVersionsResponse,
  PullListingResponse,
} from '@/types/listing';

/**
 * The listing content panel's contract mirror and API module
 * (specs/product/listing-content-panel, AUT-231). Reaches the API only
 * through `apiService` (`constraints.md § C-17`).
 */

export const listingQueryKey = (productId: number, brandId: number) =>
  ['listing', productId, brandId] as const;

export const listingVersionsQueryKey = (productId: number, brandId: number) =>
  ['listing', 'versions', productId, brandId] as const;

/**
 * Two prefixes, not one -- `"versions"` sits at index 1 of the versions
 * key, so a single array cannot prefix-match both `listingQueryKey` and
 * `listingVersionsQueryKey` for every brand id
 * (`components/product.md § The three routes`). This is the only thing the
 * broadcast handler (`WEB-002`) may pass to `invalidateQueries` -- the
 * broadcast payload carries no brand id (`data-flow.md § Flow: listing
 * content pull`, hop 10).
 */
export const listingProductQueryKeyPrefixes = (productId: number) =>
  [
    ['listing', productId],
    ['listing', 'versions', productId],
  ] as const;

/**
 * The poll rule (`data-flow.md § Flow: listing content pull`, hop 10 and
 * the table beneath it). The error clause is load-bearing and evaluated
 * first: in TanStack v5 a failed refetch of a query that already has data
 * sets `status: 'error'` and *keeps* `data`, so without this clause
 * `data.is_pulling` would stay `true` and the interval would re-arm every
 * 5 s forever, in background tabs too, at exactly the moment the endpoint
 * is failing. Copied from `scorecard.api.ts:53`.
 *
 * `enabled` is the drawer's own open state, threaded down from
 * `ProductDetail` through the panel's `active` prop. The panel stays
 * mounted while the drawer is collapsed -- that is what keeps the close
 * animation and the local cooldown state alive -- so without this gate a
 * closed, invisible panel would still `GET …/listing` on every product
 * view, and, whenever a pull is in flight, keep the 5 s
 * `refetchIntervalInBackground` poll running for nobody to see.
 */
export const useGetListing = (productId: number, brandId: number, enabled = true) =>
  useQuery<ListingResponse>({
    queryKey: listingQueryKey(productId, brandId),
    queryFn: () =>
      apiService.get<ListingResponse, { brand_id: number }>(
        `/products/hero/${productId}/listing`,
        { brand_id: brandId },
      ),
    enabled: enabled && !!productId && !!brandId,
    refetchInterval: (query) =>
      query.state.status === 'error'
        ? false
        : query.state.data?.is_pulling
          ? 5000
          : false,
    refetchIntervalInBackground: true,
  });

/**
 * `productId`, `brandId`, `enabled` -- that exact positional signature,
 * because `WEB-003`'s history modal is its only call site and passes its
 * own `open` state straight through as `enabled`.
 */
export const useGetListingVersions = (
  productId: number,
  brandId: number,
  enabled: boolean,
) =>
  useQuery<ListingVersionsResponse>({
    queryKey: listingVersionsQueryKey(productId, brandId),
    queryFn: () =>
      apiService.get<ListingVersionsResponse, { brand_id: number }>(
        `/products/hero/${productId}/listing/versions`,
        { brand_id: brandId },
      ),
    enabled,
  });

interface PullListingParams {
  productId: number;
  brandId: number;
}

/**
 * Posts an empty body to `/products/hero/{id}/listing/pull?brand_id=`.
 * Does not swallow the error -- no `onError` here, so the caller
 * (`WEB-004`) reads the mutation's own `error` to branch on the `409` and
 * `429` refusal bodies (`components/product.md § What the SPA does with
 * each refusal`); calling `handleApiError` here would toast a `409` that
 * is supposed to show no error toast at all.
 *
 * On success (`202`), invalidates the listing query so the poll can start
 * itself off the refetched `is_pulling: true`
 * (`data-flow.md § Flow: listing content pull`, "The poll starts by
 * itself"), and marks this tab as the pull's initiator so that the
 * org-wide terminal broadcast toasts here and nowhere else
 * (`listing.initiator.ts`).
 */
export const usePullListing = () =>
  useMutation<PullListingResponse, unknown, PullListingParams>({
    mutationFn: ({ productId, brandId }) =>
      apiService.post<PullListingResponse, Record<string, never>>(
        `/products/hero/${productId}/listing/pull`,
        {},
        { params: { brand_id: brandId } },
      ),
    onSuccess: (_, { productId, brandId }) => {
      // Scope `own`: this tab's own `202`, spent only by an event this
      // account triggered or one that cannot say who did
      // (`listing.initiator.ts`).
      markPullInitiated(productId, 'own');
      queryClient.invalidateQueries({ queryKey: listingQueryKey(productId, brandId) });
    },
  });
